import {FormEventHandler, useEffect, useState} from "react";
import {buildPayload} from "../state/settings.mappers";
import {SettingsPayload, SettingsState, UserOption} from "../state/settings.types";
import {makeSubmitHandler, ToastFn} from "../save/SubmitHandler";

/** Default settings used when API returns nothing (first-time setup). */
const DEFAULT_SETTINGS: SettingsState = {
    id: "0",
    users: {},
    workingDays: [],              // oder workingDays → je nach deinem State
    worktime: "",
    breaktime: "",
    maxWorkTime: 0,
    allowWorkTimeInterruption: false,
    personalAdmins: [],
    personalDelegates: [],
    vacation: 0,
    flexitime: {
        allowFlexitime: false,
        flexitimeStart: "",
        flexitimeEnd: "",
    },
    forceBreak: {
        forceBreakActive: false,
        forceBreakAfter: "",
        forceBreakTime: "",
    },
};

/**
 * Custom React hook for managing the general settings logic.
 *
 * Handles:
 * - Current settings state
 * - Form validation status
 * - Unsaved changes state
 * - Success and error states for saving
 *
 * @param {Object} params - Hook parameters
 * @param {*} params.response - API response, e.g. from ResponseSettings()
 * @param {(v: boolean) => void} [params.onChangeUnsaved] - Callback when unsaved state changes
 * @returns {{
 *   settings: SettingsState | null,
 *   validated: boolean,
 *   handlers: {
 *     handleUserChange: (name: string, selected: UserOption[]) => void,
 *     handleFlexitime: (data: { name: string; value: string | boolean }) => void,
 *     handleForceBreak: (data: { name: string; value: string | boolean }) => void,
 *     handleSwitchChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
 *     handleWorkdayChange: (selected: string[]) => void
 *   },
 *   handleSubmit: React.FormEventHandler<HTMLFormElement>,
 *   saveSuccess: boolean,
 *   saveError: string | null
 * }}
 */
export function useGeneralSettings(params: {
    response: any;
    onChangeUnsaved?: (v: boolean) => void;
    showToast: ToastFn;
    userMode: boolean;
    userId?: string;
}) {
    const {response, onChangeUnsaved, showToast, userId, userMode} = params;

    /** Current settings state or null until loaded */
    const [settings, setSettings] = useState<SettingsState | null>(null);

    /** Whether the form has been validated */
    const [validated, setValidated] = useState(false);

    /** Whether there are unsaved changes */
    const [unsaved, setUnsaved] = useState(false);

    // Load API response into state when available
    useEffect(() => {
        if (typeof response === "undefined") return;
        if (typeof response === "boolean") return;

        // Erstanlage: Backend liefert [] ⇒ jetzt DEFAULT setzen (einmalig)
        if (Array.isArray(response) && response.length === 0) {
            setSettings(DEFAULT_SETTINGS);
            setUnsaved(false);
            return;
        }

        // Normale Daten ⇒ direkt setzen (kein Merge, kein Flicker)
        setSettings(response as SettingsState);
        setUnsaved(false);
    }, [response]);


    /**
     * Marks the form as having unsaved changes and triggers the callback if provided.
     */
    const markUnsaved = () => {
        if (!unsaved) {
            setUnsaved(true);
            onChangeUnsaved?.(true);
        }
    };

    /**
     * Handles user selection changes (e.g., personal-admin / personal-delegate).
     *
     * @param {string} name - The user role key
     * @param {UserOption[]} selected - The selected users
     */
    const handleUserChange = (name: string, selected: UserOption[]) => {
        setSettings(prev => !prev ? prev : {...prev, users: {...prev.users, [name]: selected}});
        markUnsaved();
    };

    /**
     * Handles changes to flexitime settings.
     *
     * @param {{ name: string; value: string | boolean }} data - The changed field and its value
     */
    const handleFlexitime = (data: { name: string; value: string | boolean }) => {
        setSettings(prev => {
            if (!prev) return prev;
            const key = data.name.replace("flexitime-", "");
            return {
                ...prev,
                flexitime: {
                    ...prev.flexitime,
                    [key]: data.value as any,
                    ...(data.name === "allow-flexitime" ? {allowFlexitime: !!data.value} : {}),
                },
            };
        });
        markUnsaved();
    };

    /**
     * Handles changes to force break settings.
     *
     * @param {{ name: string; value: string | boolean }} data - The changed field and its value
     */
    const handleForceBreak = (data: { name: string; value: string | boolean }) => {
        setSettings(prev => {
            if (!prev) return prev;
            const key = data.name.replace("forcebreak-", "");
            return {
                ...prev,
                forceBreak: {
                    ...prev.forceBreak,
                    [key]: data.value as any,
                    ...(data.name === "forceBreakActive" ? {forceBreakActive: !!data.value} : {}),
                },
            };
        });
        markUnsaved();
    };

    /**
     * Handles switch input changes.
     *
     * @param {React.ChangeEvent<HTMLInputElement>} event - The change event
     */
    const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {name, checked} = event.target;
        if (name === "allow-worktime-interruption") {
            setSettings(prev => !prev ? prev : {...prev, allowWorkTimeInterruption: checked});
            markUnsaved();
        }
    };

    /**
     * Handles changes to working days selection.
     *
     * @param {string[]} selected - The selected working days
     */
    const handleWorkdayChange = (selected: string[]) => {
        setSettings(prev => !prev ? prev : {...prev, workingDays: selected});
        markUnsaved();
    };

    /**
     * Handles form submission, validates, and triggers save.
     *
     * @type {React.FormEventHandler<HTMLFormElement>}
     */
    const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {

        const finalRoute: string = userMode && userId
            ? `work/timesettings/save/?id=${userId}`
            : "work/timesettings/save/";

        await makeSubmitHandler<SettingsPayload>({
            showToast,
            route: finalRoute,
            buildPayload: (form: HTMLFormElement): SettingsPayload =>
                buildPayload(form, (settings as SettingsState) ?? DEFAULT_SETTINGS),
            successMessage: "Gespeichert.",
            errorMessage: "Speichern fehlgeschlagen.",
            setValidated: (isValid: boolean) => setValidated(isValid),
            setUnsaved: (v: boolean | ((p: boolean) => boolean)) => {
                setUnsaved(v);
                if (typeof v === "boolean" && !v) onChangeUnsaved?.(false);
            }
        })(event);
    };

    return {
        settings,
        validated,
        handlers: {
            handleUserChange,
            handleFlexitime,
            handleForceBreak,
            handleSwitchChange,
            handleWorkdayChange
        },
        handleSubmit,
    };
}